import { Box } from '@mui/material';
import { isTouchEnabled } from '../../../../../Theme';
import FakeProductCardV2 from '../../../../../components/Product/components/ProductCardV2/FakeProductCardV2';
import ProductCardV2 from '../../../../../components/Product/components/ProductCardV2/ProductCardV2';
import { ProductCardV2Selection } from '../../../../../components/Product/components/ProductCardV2/types/productCardV2.types';
import { CatalogProductsTestIds } from '../../../../../utils/testIds/CatalogProducts.testIds';
import { saleItemIsKit } from '../../../utils/saleItemType.utils';
import { CatalogAdjacentRows, RowCard, RowCards } from '../catalogProductsListV2.types';
import {
  CatalogMassActionsContextValue,
  CatalogSelectionItem,
  useCatalogMassActionsContext,
} from '../contexts/CatalogMassActions.context';
import { useCatalogView } from '../contexts/CatalogView.context';
import { useCatalogListArrangement } from '../hooks/useCatalogListArrangement';
import { CatalogDropIndicatorState } from '../hooks/useCatalogProductsDragAndDrop.types';
import CatalogKitListRow from '../list/components/CatalogKitListRow';
import { CatalogListRowAdjacentSelection } from '../list/components/CatalogProductListRow';
import CatalogProductListRowConnected from '../list/components/CatalogProductListRowConnected';
import CatalogProductListRowSkeleton from '../list/components/CatalogProductListRowSkeleton';
import { getCatalogListRowHeight } from '../list/utils/catalogListRow.consts';
import { DropPointerSide } from '../utils/catalogDragAndDrop.types';
import { CatalogViewMode } from '../view/catalogView.types';
import CatalogDropIndicator, {
  INDICATOR_THICKNESS,
} from './CatalogDropIndicator/CatalogDropIndicator';
import DraggableProductCard from './DraggableProductCard/DraggableProductCard';
import {
  buildProductCardProps,
  getCatalogSelectionSectionProductId,
} from './catalogProductRowV2.utils';

interface CatalogRowDragConfig {
  activeId: string | null;
  isDraggingDisabled: boolean;
  dropIndicator: CatalogDropIndicatorState | null;
}

interface CatalogProductRowV2Props {
  catalogId: string;
  row: RowCards;
  rowIndex: number;
  adjacentRows?: CatalogAdjacentRows;
  drag?: CatalogRowDragConfig;
}

const isAdjacentRowSelected = (
  massActions: CatalogMassActionsContextValue,
  row: RowCards,
  adjacent?: RowCards,
) => {
  if (!adjacent || adjacent.sectionId !== row.sectionId) {
    return false;
  }

  const [card] = adjacent.cards;

  return card?.kind === 'item' && massActions.isSelected(card.esProduct.id);
};

const buildCardSelection = (
  massActions: CatalogMassActionsContextValue,
  item: CatalogSelectionItem,
): ProductCardV2Selection | undefined => {
  if (massActions.readMode) {
    return undefined;
  }

  return {
    isSelected: massActions.isSelected(item.productId),
    isSelectionActive: massActions.isSelectionActive,
    isLocked: massActions.isSelectionLocked,
    onToggle: () => massActions.toggle(item),
  };
};

const resolveDropIndicatorSide = (
  dropIndicator: CatalogDropIndicatorState | null | undefined,
  card: RowCard,
): DropPointerSide | null => {
  if (!dropIndicator || card.kind !== 'item' || dropIndicator.overId !== card.esProduct.id) {
    return null;
  }

  return dropIndicator.side;
};

const CatalogProductRowV2 = ({
  catalogId,
  row,
  rowIndex,
  adjacentRows,
  drag,
}: CatalogProductRowV2Props) => {
  const massActions = useCatalogMassActionsContext();
  const { variant, effectiveMode } = useCatalogView();
  const { isStacked } = useCatalogListArrangement();

  const renderItemCard = (card: Extract<RowCard, { kind: 'item' }>) => {
    const selection = buildCardSelection(massActions, {
      productId: card.esProduct.id,
      sectionProductId: getCatalogSelectionSectionProductId(card.esProduct),
    });
    const productCard = { ...buildProductCardProps(card, catalogId), selection, size: variant.key };

    if (!drag) {
      return <ProductCardV2 key={card.esProduct.id} {...productCard} />;
    }

    const indicatorSide = resolveDropIndicatorSide(drag.dropIndicator, card);

    return (
      <Box key={card.esProduct.id} sx={{ position: 'relative', height: '100%' }}>
        {indicatorSide && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              zIndex: 1,
              width: INDICATOR_THICKNESS,
              ...(indicatorSide === 'before' ? { left: 0 } : { right: 0 }),
            }}
          >
            <CatalogDropIndicator orientation="vertical" />
          </Box>
        )}

        <DraggableProductCard
          productCard={productCard}
          drag={{
            id: card.esProduct.id,
            activeId: drag.activeId,
            isDraggingDisabled: drag.isDraggingDisabled,
          }}
        />
      </Box>
    );
  };

  const renderCard = (card: RowCard, col: number) => {
    switch (card.kind) {
      case 'item': {
        return renderItemCard(card);
      }

      case 'skeleton': {
        return <FakeProductCardV2 key={`skeleton-${rowIndex}-${col}`} size={variant.key} />;
      }

      case 'empty': {
        return <div key={`empty-${rowIndex}-${col}`} test-id={CatalogProductsTestIds.EMPTY_SLOT} />;
      }
    }
  };

  const adjacentSelection: CatalogListRowAdjacentSelection = {
    isPreviousSelected: isAdjacentRowSelected(massActions, row, adjacentRows?.previous),
    isNextSelected: isAdjacentRowSelected(massActions, row, adjacentRows?.next),
  };

  const renderListCard = (card: RowCard) => {
    switch (card.kind) {
      case 'item': {
        const { item } = card.esProduct;

        if (saleItemIsKit(item)) {
          return (
            <CatalogKitListRow
              catalogId={catalogId}
              card={card}
              kit={item}
              isStacked={isStacked}
              adjacentSelection={adjacentSelection}
              drag={drag && { isDraggingDisabled: drag.isDraggingDisabled }}
            />
          );
        }

        return (
          <CatalogProductListRowConnected
            catalogId={catalogId}
            card={card}
            productColor={item}
            isStacked={isStacked}
            adjacentSelection={adjacentSelection}
            drag={drag && { isDraggingDisabled: drag.isDraggingDisabled }}
          />
        );
      }

      case 'skeleton': {
        return (
          <CatalogProductListRowSkeleton
            isStacked={isStacked}
            hasSelection={!massActions.readMode}
          />
        );
      }

      case 'empty': {
        return <Box test-id={CatalogProductsTestIds.EMPTY_SLOT} sx={{ height: '100%' }} />;
      }
    }
  };

  if (effectiveMode === CatalogViewMode.LIST) {
    const [card] = row.cards;
    const indicatorSide = resolveDropIndicatorSide(drag?.dropIndicator, card);
    const isActiveRow = card.kind === 'item' && drag?.activeId === card.esProduct.id;

    return (
      <Box
        test-id={CatalogProductsTestIds.PRODUCT_ROW}
        sx={{
          height: getCatalogListRowHeight({ isStacked, isTouchEnabled }),
          position: 'relative',
          opacity: isActiveRow ? 0.4 : 1,
        }}
      >
        {indicatorSide && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: INDICATOR_THICKNESS,
              zIndex: 1,
              ...(indicatorSide === 'before' ? { top: 0 } : { bottom: 0 }),
            }}
          >
            <CatalogDropIndicator orientation="horizontal" />
          </Box>
        )}

        {renderListCard(card)}
      </Box>
    );
  }

  return (
    <Box
      test-id={CatalogProductsTestIds.PRODUCT_ROW}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${row.cards.length}, minmax(0, ${variant.maxWidth}px))`,
        justifyContent: 'center',
        gap: 1.75,
        alignItems: 'stretch',
        paddingBottom: 4,
        paddingLeft: '4px',
        paddingRight: '4px',
      }}
    >
      {row.cards.map(renderCard)}
    </Box>
  );
};

export default CatalogProductRowV2;
